import apiClient from './api.service';

export interface FiltersCriteria {
  createdFrom?: string;
  createdTo?: string;
  active?: boolean;
  gender?: 'MALE' | 'FEMALE';
  ageRangeKey?: string;
  educationLevel?: string;
  stateIds?: number[];
  cityIds?: number[];
}

export type PreviewRequest =
  | ({ source: 'FILTERS' } & FiltersCriteria)
  | { source: 'SURVEY'; surveyId: number };

export interface PreviewResponse {
  recipientCount: number;
}

export interface CreateFiltersCampaignPayload extends FiltersCriteria {
  source: 'FILTERS';
  subject: string;
  contentHtml: string;
}

export interface CreateSurveyCampaignPayload {
  source: 'SURVEY';
  surveyId: number;
  subject: string;
  contentHtml: string;
}

export interface CreateJsonCampaignPayload {
  source: 'JSON';
  subject: string;
  contentHtml: string;
  file: File;
}

export type CreateCampaignPayload =
  | CreateFiltersCampaignPayload
  | CreateSurveyCampaignPayload
  | CreateJsonCampaignPayload;

export interface CreateCampaignResponse {
  campaignId: number;
  totalRecipients: number;
  status: string;
}

export interface CampaignSummary {
  id: number;
  source: 'JSON' | 'SURVEY' | 'FILTERS';
  subject: string;
  status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'COMPLETED_WITH_FAILURES' | 'FAILED';
  totalRecipients: number;
  sentCount: number;
  failedCount: number;
  suppressedCount: number;
  createdAt: string;
  adminUserName: string;
  canResume: boolean;
}

export interface ResumeCampaignResponse {
  campaignId: number;
  status: 'PROCESSING';
}

export interface FinalizeCampaignResponse {
  campaignId: number;
  status: 'COMPLETED_WITH_FAILURES';
}

export interface CampaignListResponse {
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
  items: CampaignSummary[];
}

export interface CampaignDetail extends CampaignSummary {
  contentHtml: string;
  surveyId: number | null;
  filterCriteria: unknown;
  startedAt: string | null;
  completedAt: string | null;
}

export interface RecipientItem {
  id: number;
  email: string;
  status: 'PENDING' | 'SENT' | 'FAILED' | 'SUPPRESSED_OPT_OUT' | 'SUPPRESSED_SES';
  failureReason: string | null;
  processedAt: string | null;
}

export interface RecipientListResponse {
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
  items: RecipientItem[];
}

export async function previewRecipients(payload: PreviewRequest): Promise<PreviewResponse> {
  const response = await apiClient.post<{ data: PreviewResponse }>(
    '/api/admin/bulk-email/preview',
    payload
  );
  return response.data.data;
}

export async function createCampaign(
  payload: CreateCampaignPayload
): Promise<CreateCampaignResponse> {
  if (payload.source === 'JSON') {
    const formData = new FormData();
    formData.append('source', 'JSON');
    formData.append('subject', payload.subject);
    formData.append('contentHtml', payload.contentHtml);
    formData.append('file', payload.file);

    const response = await apiClient.post<{ data: CreateCampaignResponse }>(
      '/api/admin/bulk-email/campaigns',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data.data;
  }

  const response = await apiClient.post<{ data: CreateCampaignResponse }>(
    '/api/admin/bulk-email/campaigns',
    payload
  );
  return response.data.data;
}

export async function listCampaigns(
  params: { page?: number; pageSize?: number; status?: string } = {}
): Promise<CampaignListResponse> {
  const response = await apiClient.get<{ data: CampaignListResponse }>(
    '/api/admin/bulk-email/campaigns',
    { params }
  );
  return response.data.data;
}

export async function getCampaign(campaignId: number): Promise<CampaignDetail> {
  const response = await apiClient.get<{ data: CampaignDetail }>(
    `/api/admin/bulk-email/campaigns/${campaignId}`
  );
  return response.data.data;
}

export async function resumeCampaign(campaignId: number): Promise<ResumeCampaignResponse> {
  const response = await apiClient.post<{ data: ResumeCampaignResponse }>(
    `/api/admin/bulk-email/campaigns/${campaignId}/resume`
  );
  return response.data.data;
}

export async function finalizeCampaign(campaignId: number): Promise<FinalizeCampaignResponse> {
  const response = await apiClient.post<{ data: FinalizeCampaignResponse }>(
    `/api/admin/bulk-email/campaigns/${campaignId}/finalize`
  );
  return response.data.data;
}

export async function listCampaignRecipients(
  campaignId: number,
  params: { status?: string; page?: number; pageSize?: number } = {}
): Promise<RecipientListResponse> {
  const response = await apiClient.get<{ data: RecipientListResponse }>(
    `/api/admin/bulk-email/campaigns/${campaignId}/recipients`,
    { params }
  );
  return response.data.data;
}
